import { type Accept } from "react-dropzone";
import {
  Controller,
  useController,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type UseControllerProps,
  type UseFormSetValue,
} from "react-hook-form";
import { Dropzone } from "@components/Dropzone/Dropzone";
import { type IInputProps } from "@components/inputs/Input/Input";
import { Notification } from "@components/Notification/Notification";

type TFormInputProps<T extends FieldValues> = IInputProps &
  UseControllerProps<T> & {
    setValue: UseFormSetValue<T>;
  } & {
    acceptedFiles?: Accept;
  };

export const FileUploadButton = <
  T extends FieldValues,
  TFieldName extends FieldPath<T>,
>({
  name,
  control,
  setValue,
  acceptedFiles,
  ...rest
}: TFormInputProps<T>) => {
  const controller = useController({
    name,
    control,
  });
  const {
    fieldState: { error },
  } = controller;

  return (
    <Controller
      name={name}
      control={control}
      render={() => (
        <>
          <Dropzone
            onDrop={(acceptedFiles: FileList) => {
              setValue(
                name,
                acceptedFiles[0] as FieldPathValue<T, TFieldName>,
                {
                  shouldValidate: true,
                },
              );
            }}
            acceptedFiles={acceptedFiles}
            {...rest}
          />
          {error?.message && (
            <Notification
              color="red"
              variant="small"
              description={error.message}
            />
          )}
        </>
      )}
    />
  );
};
